import { SRGBColorSpace, Mesh, MeshBasicMaterial, PlaneGeometry, DoubleSide, Vector3, Group } from 'three';
import WebGLGroup from '../_app/cuchillo/3D/WebGLGroup';
import ModelGLB from '../_app/cuchillo/3D/ModelGLB';
import TexturesController from '../_app/cuchillo/3D/TexturesController';
import GeometryController from '../_app/cuchillo/3D/GeometryController';
import CanvasColorTexture from '../_app/cuchillo/3D/CanvasColorTexture';
import { Basics, isTouch } from '../_app/cuchillo/core/Basics';
import { Interaction } from '../_app/cuchillo/core/Interaction';

export const SPIN_SETTINGS = {
    intensity: 0.001,
    friction: 0.961,
};

// Multiplicador de mida del plane frontal (label). Tunejable des del DebugPane per ajustar-lo
// a ull; un cop trobat el valor bo, congelar aquí com a constant i treure el control del pane.
export const FRONT_PLANE_SETTINGS = {
    scale: 1,
};

export default class Box extends ModelGLB {
    tick = 0;
    posZ = 0;
    boxGroup = new WebGLGroup({ rotFactor: .07, zFactor: .1 });
    spinGroup = new WebGLGroup({ rotFactor: 0.08 });

    _spinVelX = 0;
    _spinVelY = 0;
    _spinAccumX = 0;
    _spinAccumY = 0;
    _isMouseDown = false;
    _prevMouseX = 0;
    _prevMouseY = 0;

    constructor(opts = {}) {
        super(opts);
        this.model2 = opts.model2 || null;
        this.diffusion2 = opts.diffusion2 || null;
        this.hasDrag = opts.hasDrag ?? false;
        this.hasFlotation = opts.hasFlotation ?? false;
        // frontPlane: afegeix un Plane a la cara frontal del box (mateix width/height). De moment
        // amb una textura de canvas ff0000; més endavant serà la imatge/label definitiva (Image).
        this.hasFrontPlane = opts.hasFrontPlane ?? false;
        // srgbDiffuse: marca les textures de diffuse com a sRGB (three r152+ no les decodifica per
        // defecte → es veurien rentades). Cal per a materials UNLIT (MeshBasicMaterial, p.ex. __obj),
        // que mostren la textura crua; els materials amb llum ja s'ajusten via il·luminació.
        this.srgbDiffuse = opts.srgbDiffuse ?? false;

        this.spinGroup.add(this.boxGroup);
        this.add(this.spinGroup);

        if (this.hasDrag) {
            this._onDown = this._handleDown.bind(this);
            this._onUp = this._handleUp.bind(this);
            this._onMove = this._handleMove.bind(this);

            document.addEventListener(Basics.downEvent, this._onDown);
            document.addEventListener(Basics.upEvent, this._onUp);
            document.addEventListener(Basics.moveEvent, this._onMove);
        }
    }

    _handleDown(e) {
        const mx = isTouch ? e.touches[0].clientX : e.clientX;
        const my = isTouch ? e.touches[0].clientY : e.clientY;
        const { x, y, width, height } = this.dom.getBoundingClientRect();

        if (mx >= x && mx <= x + width && my >= y && my <= y + height) {
            this._isMouseDown = true;
            this._prevMouseX = Interaction.positions.mouse.x;
            this._prevMouseY = Interaction.positions.mouse.y;
            document.body.classList.add('--box-dragging');
        }
    }

    _handleUp() {
        this._isMouseDown = false;
        document.body.classList.remove('--box-dragging');
    }

    _handleMove() {
        if (!this._isMouseDown) return;

        const mx = Interaction.positions.mouse.x;
        const my = Interaction.positions.mouse.y;

        this._spinVelY += (mx - this._prevMouseX) * SPIN_SETTINGS.intensity;

        this._prevMouseX = mx;
        this._prevMouseY = my;
    }

    init() {
        const loadGLB = (src) => new Promise((resolve) => {
            GeometryController.load({ src, call: resolve });
        });

        const jobs = [loadGLB(this.model)];
        if (this.model2) jobs.push(loadGLB(this.model2));

        return Promise.all(jobs).then(([gltf1, gltf2]) => {
            this.object = gltf1;
            this.mesh = gltf1.scene.children[0];
            this.boxGroup.add(gltf1.scene);

            if (gltf2) {
                this.object2 = gltf2;
                // if model2 is separate, take its first child; otherwise fall back to sibling mesh
                this.mesh2 = gltf2.scene.children[0] ?? null;
                this.boxGroup.add(gltf2.scene);
            } else {
                // single GLB with 2 internal meshes
                this.mesh2 = gltf1.scene.children[1] ?? null;
            }

            if (this._material) {
                this._collectMeshes().forEach(m => { m.material = this._material.clone(); });
            }

            this.size.copy(this.mesh.scale);
            this.resize();
            this.loadTextures();

            // Després de loadTextures() (que ja ha capturat els meshes del model) perquè el plane
            // no s'inclogui a _collectMeshes ni rebi les textures diffuse/specular del box.
            if (this.hasFrontPlane) this._addFrontPlane();
        });
    }

    // Afegeix un Plane a la cara frontal del box, amb el mateix width i height que aquesta cara.
    // El plane NO és fill del glb: va en un grup propi (planeGroup) penjat de boxGroup, agrupat amb
    // el box però fora de l'escena del model. El planeGroup replica la transformada del mesh del glb
    // (quaternion + posició + escala del resize) perquè el plane quedi ben orientat i dimensionat.
    // A box-4.glb la cara gran és perpendicular a l'eix Y local (l'eix prim del model). La cara
    // visible és la +Y local: amb hasFlotation el boxGroup gira Math.PI al voltant de Y, així que la
    // cara +Y queda mirant la càmera. Orientem el plane amb rotateX i el col·loquem a max.y local.
    // width = extensió X local, height = extensió Z local.
    _addFrontPlane() {
        if (!this.mesh || !this.mesh.geometry) return;

        const geo = this.mesh.geometry;
        if (!geo.boundingBox) geo.computeBoundingBox();
        const bbox = geo.boundingBox;

        const size = bbox.getSize(new Vector3());
        const center = bbox.getCenter(new Vector3());

        const planeGeo = new PlaneGeometry(size.x, size.z);
        planeGeo.rotateX(-Math.PI / 2); // normal +Z → +Y local (cara visible cap a la càmera)

        const material = new MeshBasicMaterial({
            map: new CanvasColorTexture('#ff0000').texture, // fallback fins que carregui el label
            side: DoubleSide,
            transparent: true, // el label PNG porta alpha
        });

        const EPS = size.y * 0.01; // petit offset cap a fora (+Y) per evitar z-fighting amb la cara
        this.frontPlane = new Mesh(planeGeo, material);
        this.frontPlane.position.set(center.x, bbox.max.y + EPS, center.z);
        this.frontPlane.scale.setScalar(FRONT_PLANE_SETTINGS.scale); // ajust de mida (DebugPane)

        // Copia el mapatge UV REAL de la cara frontal del glb al plane: així mostra exactament la
        // mateixa zona de la textura que el box (mateixa posició, escala i rotació d'UV) → el
        // contingut queda alineat automàticament, sense centrar res a mà ni res content-aware.
        this._applyFaceUVToPlane(planeGeo);

        // Grup propi fora del glb que replica la transformada del mesh del model (assumeix que
        // l'escena del glb és identitat, que és el cas de box-4.glb). L'escala se sincronitza a resize().
        this.planeGroup = new Group();
        this.planeGroup.quaternion.copy(this.mesh.quaternion);
        this.planeGroup.position.copy(this.mesh.position);
        this.planeGroup.scale.copy(this.mesh.scale);
        this.planeGroup.add(this.frontPlane);
        this.boxGroup.add(this.planeGroup);

        // Textura del label (data-label al .__box): és la textura COMPLETA del box (mateixa
        // disposició UV que el diffuse), transparent excepte el contingut central. Com que el plane
        // ja té els UVs reals de la cara, la textura mapeja idèntica al box. MeshBasicMaterial és
        // unlit → sRGB perquè el color coincideixi; flipY=false per igualar la convenció del glb.
        const labelSrc = this.dom?.dataset.label;
        if (labelSrc) {
            TexturesController.load({
                src: labelSrc,
                call: (texture) => {
                    texture.colorSpace = SRGBColorSpace;
                    texture.flipY = false;
                    texture.needsUpdate = true;
                    material.map = texture;
                    material.needsUpdate = true;
                }
            });
        }
    }

    // Ajusta els UVs del plane perquè mapegin la textura EXACTAMENT com ho fa la cara frontal del box.
    // La cara frontal (+Y exterior, y≈maxY) mapeja (x,z)→(u,v) de forma afí; ho resolem per mínims
    // quadrats sobre aquests vèrtexs i apliquem la mateixa transformació als vèrtexs del plane. Filtrar
    // per y≈maxY és clau: hi ha superfícies internes amb normal +Y però UVs d'una altra illa.
    _applyFaceUVToPlane(planeGeo) {
        const geo = this.mesh?.geometry;
        const pos = geo?.attributes.position;
        const nrm = geo?.attributes.normal;
        const uv = geo?.attributes.uv;
        if (!pos || !nrm || !uv) return false;

        if (!geo.boundingBox) geo.computeBoundingBox();
        const maxY = geo.boundingBox.max.y;
        const tol = (geo.boundingBox.max.y - geo.boundingBox.min.y) * 0.05;

        // Acumuladors per resoldre u = a·x + b·z + c i v = d·x + e·z + f (normal equations 3×3).
        let Sxx = 0, Sxz = 0, Sx = 0, Szz = 0, Sz = 0, S1 = 0;
        let Bxu = 0, Bzu = 0, Bu = 0, Bxv = 0, Bzv = 0, Bv = 0;
        for (let i = 0; i < pos.count; i++) {
            if (nrm.getY(i) < 0.99) continue;                      // només la cara +Y
            if (Math.abs(pos.getY(i) - maxY) > tol) continue;      // ...i només la superfície exterior
            const x = pos.getX(i), z = pos.getZ(i), u = uv.getX(i), v = uv.getY(i);
            Sxx += x * x; Sxz += x * z; Sx += x; Szz += z * z; Sz += z; S1++;
            Bxu += x * u; Bzu += z * u; Bu += u; Bxv += x * v; Bzv += z * v; Bv += v;
        }
        if (S1 < 3) return false;

        const M = [[Sxx, Sxz, Sx], [Sxz, Szz, Sz], [Sx, Sz, S1]];
        const det3 = (m) =>
            m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
            m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
            m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0]);
        const D = det3(M);
        if (Math.abs(D) < 1e-12) return false;
        const solve = (b) => {
            const col = (ci) => {
                const mm = M.map(r => r.slice());
                for (let r = 0; r < 3; r++) mm[r][ci] = b[r];
                return det3(mm) / D;
            };
            return [col(0), col(1), col(2)];
        };
        const pu = solve([Bxu, Bzu, Bu]);
        const pv = solve([Bxv, Bzv, Bv]);

        // El plane és fill del planeGroup (mateix espai que el mesh-local); un vèrtex del plane és a
        // (position + vèrtex-geometria). Apliquem-hi el mateix mapatge afí per obtenir-ne els UVs.
        const ppos = planeGeo.attributes.position;
        const puv = planeGeo.attributes.uv;
        const ox = this.frontPlane.position.x, oz = this.frontPlane.position.z;
        for (let i = 0; i < ppos.count; i++) {
            const x = ox + ppos.getX(i), z = oz + ppos.getZ(i);
            puv.setXY(i, pu[0] * x + pu[1] * z + pu[2], pv[0] * x + pv[1] * z + pv[2]);
        }
        puv.needsUpdate = true;
        return true;
    }

    _collectMeshes() {
        const meshes = [];
        if (this.mesh) this.mesh.traverse(o => { if (o.isMesh) meshes.push(o); });
        // If a second GLB was loaded, collect its meshes too (appended after first)
        if (this.object2) {
            this.object2.scene.children[0]?.traverse(o => { if (o.isMesh) meshes.push(o); });
        }
        return meshes;
    }

    loadTextures() {
        const meshes = this._collectMeshes();

        if (this.diffusion && meshes[0]) {
            TexturesController.load({
                src: this.diffusion,
                call: (texture) => {
                    texture.flipY = false;
                    if (this.srgbDiffuse) { texture.colorSpace = SRGBColorSpace; texture.needsUpdate = true; }
                    if (!this._material) meshes[0].material = meshes[0].material.clone();
                    meshes[0].material.map = texture;
                    meshes[0].material.needsUpdate = true;
                }
            });
        }

        if (this.diffusion2 && meshes[1]) {
            TexturesController.load({
                src: this.diffusion2,
                call: (texture) => {
                    texture.flipY = false;
                    if (this.srgbDiffuse) { texture.colorSpace = SRGBColorSpace; texture.needsUpdate = true; }
                    if (!this._material) meshes[1].material = meshes[1].material.clone();
                    meshes[1].material.map = texture;
                    meshes[1].material.needsUpdate = true;
                }
            });
        }

        if (this.specular) {
            TexturesController.load({
                src: this.specular,
                call: (texture) => {
                    texture.flipY = false;
                    meshes.forEach(o => {
                        if (!this._material) o.material = o.material.clone();
                        o.material.specularMap = texture;
                        o.material.needsUpdate = true;
                    });
                }
            });
        }
    }

    update() {
        this.tick++;

        this._spinVelY *= SPIN_SETTINGS.friction;
        this._spinAccumY += this._spinVelY;
        this.spinGroup.rot.y = this._spinAccumY;
        this.spinGroup.update();

        if (this.hasFlotation) {
            this.boxGroup.rot.x = -.3 + Math.sin(this.tick * .01) * .15;
            this.boxGroup.rot.y = Math.PI + Math.sin(this.tick * .006) * .15;
            this.boxGroup.rot.z = Math.sin(this.tick * .005) * .08;
            this.boxGroup.pos.x = -2 + Math.sin(this.tick * .01) * 4;
            this.boxGroup.pos.y = -10 + Math.sin(this.tick * .02) * 20;
            this.boxGroup.pos.z = -2 + Math.sin(this.tick * .01) * 4;
        }

        this.boxGroup.update();

        super.update();
    }

    dispose() {
        if (this.hasDrag) {
            document.removeEventListener(Basics.downEvent, this._onDown);
            document.removeEventListener(Basics.upEvent, this._onUp);
            document.removeEventListener(Basics.moveEvent, this._onMove);
        }
        super.dispose();
    }

    resize() {
        super.resize();
        this.dataZ = this.size.x * this.sizeFactor;
        // El planeGroup viu fora del glb → cal replicar l'escala que resize() aplica al mesh del model.
        if (this.planeGroup && this.mesh) this.planeGroup.scale.copy(this.mesh.scale);
    }
}
